/**
 * Tiny IndexedDB blob store for drawing photos (localStorage is too small for images).
 * Falls back to an in-memory map when IndexedDB is unavailable (private mode, tests).
 */
const DB = 'sketchling-images';
const STORE = 'images';
/** Small LRU of recent blobs (fast re-reads); the only store when IndexedDB is unavailable. */
const memory = new Map<string, Blob>();
const urls = new Map<string, string>();
const MEMORY_MAX = 24;
let noDb = false;
function remember(key: string, blob: Blob) {
  memory.delete(key);
  memory.set(key, blob);
  if (noDb) return; // fallback store: keep everything
  while (memory.size > MEMORY_MAX) memory.delete(memory.keys().next().value!);
}
/** Called when the device is out of space for photos (quota): the UI tells the grown-up. */
let quotaListener: ((key: string) => void) | null = null;
export const onImageQuota = (fn: (key: string) => void) => void (quotaListener = fn);
let dbp: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (dbp) return dbp;
  dbp = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbp;
}

export async function putImage(key: string, blob: Blob): Promise<void> {
  const db = await open();
  if (!db) noDb = true;
  remember(key, blob);
  if (!db) return;
  await new Promise<void>((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(blob, key);
    } catch {
      return resolve();
    }
    tx.oncomplete = () => resolve();
    const fail = () => {
      const name = tx.error?.name ?? '';
      if (/Quota/i.test(name)) quotaListener?.(key);
      resolve();
    };
    tx.onerror = fail;
    tx.onabort = fail;
  });
}

export async function getImage(key: string): Promise<Blob | null> {
  const m = memory.get(key);
  if (m) return m;
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => {
      const b = (req.result as Blob | undefined) ?? null;
      if (b) remember(key, b);
      resolve(b);
    };
    req.onerror = () => resolve(null);
  });
}

export async function deleteImage(key: string): Promise<void> {
  memory.delete(key);
  const u = urls.get(key);
  if (u) (URL.revokeObjectURL(u), urls.delete(key));
  const db = await open();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

export async function clearImages(): Promise<void> {
  memory.clear();
  const db = await open();
  if (!db) return;
  await new Promise<void>((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

/**
 * Object-URL cache so lists don't create a URL per render. Bounded (an object URL keeps its photo in memory): the
 * oldest URLs are revoked; an <img> that already shows one keeps its pixels, and a remount asks again.
 */
const URLS_MAX = 120;
export async function imageUrl(key: string): Promise<string | null> {
  const cached = urls.get(key);
  if (cached) {
    urls.delete(key);
    urls.set(key, cached);
    return cached;
  }
  const b = await getImage(key);
  if (!b) return null;
  const u = URL.createObjectURL(b);
  urls.set(key, u);
  while (urls.size > URLS_MAX) {
    const [k, old] = urls.entries().next().value!;
    urls.delete(k);
    URL.revokeObjectURL(old);
  }
  return u;
}

/** Ask the browser to keep the photos (no eviction under storage pressure). Web only; harmless elsewhere. */
export async function persistStorage(): Promise<void> {
  try {
    if (navigator.storage?.persisted && !(await navigator.storage.persisted())) await navigator.storage.persist?.();
  } catch {
    /* unsupported */
  }
}
